/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Checkbox } from 'carbon-react/lib/components/checkbox';

import { useReportBuilderContext } from '../../report-builder.context';
import { CARD_TYPES, WIDGET_TYPES } from '../../types';
import { ColorPicker } from '../../../forecast/detail/components/color-picker';
import { generateDefaultColors, shadeColor } from '../../utils-colors';
import { getConsolidationShortCode } from '../../utils-data';
import { useConsolidatedForecastingContext } from '../../../forecast/detail/context/consolidated-forecasting-context';

import styles from './theme-customization.scss';

export const ChartThemeCustomization = () => {
  const intl = useIntl();
  const {
    currentCard,
    setFinFieldOptions,
    reportColors,
    selectedFinFieldsIncChildren,
    getSelectedDatasets,
    getDatasetName,
    setWidgetDataByKey
  } = useReportBuilderContext();
  const { consolidation } = useConsolidatedForecastingContext();
  const selectedDatasets = getSelectedDatasets();
  const darkningStep = selectedDatasets.length > 1 ? 0.8 / selectedDatasets.length : 0;
  const widgetOptions = currentCard && currentCard[CARD_TYPES.WIDGET];
  const isBarChart = [
    WIDGET_TYPES.BAR_HORIZONTAL,
    WIDGET_TYPES.BAR_MULTI_HORIZONTAL,
    WIDGET_TYPES.BAR_MULTI_VERTICAL,
    WIDGET_TYPES.BAR_VERTICAL
  ].includes(widgetOptions?.widgetType as WIDGET_TYPES);
  const isLineChart = [WIDGET_TYPES.AREA, WIDGET_TYPES.LINE, WIDGET_TYPES.LINE_HORIZONTAL].includes(
    widgetOptions?.widgetType as WIDGET_TYPES
  );

  const chartColors = useMemo(() => generateDefaultColors(selectedFinFieldsIncChildren.length), [
    selectedFinFieldsIncChildren.length
  ]);

  const [datasetsColors, setDatasetsColors] = useState<{ id: string; color: string }[]>(
    widgetOptions?.forecastStartLineColors || []
  );
  const [customColors, setCustomColors] = useState<string[][]>(
    selectedFinFieldsIncChildren.map((ff, i) => {
      const wc = widgetOptions?.finFieldsOptions.find(e => e.ref === ff.ref)?.options?.colors || {};
      if (Array.isArray(wc)) {
        return selectedDatasets.map((_, j) =>
          i === 0 ? wc[0] || chartColors[i] : shadeColor(chartColors[i], 1 + j * darkningStep)
        );
      } else {
        return selectedDatasets.map((f, j) => wc[f] || shadeColor(chartColors[i], 1 + j * darkningStep));
      }
    })
  );

  const handleColorChange = (ref: string, color: string, i: number, forI: number, forId: string) => {
    if (color !== customColors[i][forI]) {
      const colorsCopy = [...customColors];
      colorsCopy[i] = [...colorsCopy[i]];
      colorsCopy[i][forI] = color;
      setCustomColors(colorsCopy);
      const wc = widgetOptions?.finFieldsOptions.find(e => e.ref === ref)?.options?.colors || {};
      let prevColors: Record<string, string> = {};
      if (!Array.isArray(wc)) {
        prevColors = wc;
      }

      setFinFieldOptions(ref, { colors: { ...prevColors, [forId]: color } });
    }
  };

  const handleDatasetColorChange = (dsId: string, color: string) => {
    if (color !== datasetsColors.find(d => d.id === dsId)?.color) {
      const colorsCopy = [...datasetsColors];
      const idx = colorsCopy.findIndex(d => d.id === dsId);
      if (idx === -1) {
        colorsCopy.push({ id: dsId, color });
      } else {
        colorsCopy[idx] = { id: dsId, color };
      }
      setDatasetsColors(colorsCopy);
      setWidgetDataByKey('forecastStartLineColors', colorsCopy);
    }
  };

  const handleShowZeroLineChange = (_event: React.ChangeEvent<HTMLInputElement>) => {
    setWidgetDataByKey('showZeroLine', !widgetOptions?.showZeroLine);
  };
  const handleShowBarLabelChange = (_event: React.ChangeEvent<HTMLInputElement>) => {
    setWidgetDataByKey('showBarLabels', !widgetOptions?.showBarLabels);
  };
  const handleShowForecastStartLineChange = (_event: React.ChangeEvent<HTMLInputElement>) => {
    setWidgetDataByKey('showForecastStartLine', !widgetOptions?.showForecastStartLine);
  };

  return widgetOptions ? (
    <>
      <div>
        {(isBarChart || isLineChart) && (
          <h3 className={styles.title}>
            <Checkbox
              checked={!!widgetOptions?.showZeroLine}
              onChange={handleShowZeroLineChange}
              aria-label={intl.formatMessage({ id: 'reporting.show_zero_line', defaultMessage: 'Show zero line' })}
              label={intl.formatMessage({ id: 'reporting.show_zero_line', defaultMessage: 'Show zero line' })}
              m={1}
            />
          </h3>
        )}
        {isBarChart && (
          <h3 className={styles.title}>
            <Checkbox
              checked={!!widgetOptions?.showBarLabels}
              onChange={handleShowBarLabelChange}
              aria-label={intl.formatMessage({
                id: 'reporting.show_bar_labels',
                defaultMessage: 'Show value labels (if bars are wide enough)'
              })}
              label={intl.formatMessage({
                id: 'reporting.show_bar_labels',
                defaultMessage: 'Show value labels (if bars are wide enough)'
              })}
              m={1}
            />
          </h3>
        )}
        {(isBarChart || isLineChart) && (
          <h3 className={styles.title}>
            <Checkbox
              checked={!!widgetOptions?.showForecastStartLine}
              onChange={handleShowForecastStartLineChange}
              aria-label={intl.formatMessage({
                id: 'reporting.show_actuals_forecasts_separator',
                defaultMessage: 'Show actuals and forecasts separator'
              })}
              label={intl.formatMessage({
                id: 'reporting.show_actuals_forecasts_separator',
                defaultMessage: 'Show actuals and forecasts separator'
              })}
              m={1}
            />
          </h3>
        )}
      </div>
      <div className={styles.themeCustomization}>
        <h3 className={styles.title}>
          <FormattedMessage id="reporting.chart_colours" defaultMessage="Chart colours" />
        </h3>
        {selectedDatasets.map(ds => (
          <div key={`dataset-color-${ds}`} className={styles.colorContainer}>
            <label htmlFor={`dataset-color-${ds}`}>{getDatasetName(ds)}</label>
            <ColorPicker
              id={`dataset-${ds}-custom-color`}
              color={datasetsColors.find(d => d.id === ds)?.color || '#000'}
              onChange={e => handleDatasetColorChange(ds, e)}
              presetColors={reportColors}
            />
          </div>
        ))}
        {selectedFinFieldsIncChildren.map((ff, i) => {
          if (!selectedDatasets?.length) {
            return null;
          }
          return selectedDatasets.length === 1 ||
            [WIDGET_TYPES.DONUT, WIDGET_TYPES.PIE].includes(widgetOptions.widgetType as WIDGET_TYPES) ? (
            <div key={ff.ref} className={styles.colorContainer}>
              <label htmlFor={ff.ref}>
                {ff.name}
                {ff.organisation_id ? ` - ${getConsolidationShortCode(consolidation, ff.organisation_id)}` : ''}
              </label>
              <ColorPicker
                id={`${ff.ref}-custom-color`}
                color={customColors[i]?.[0]}
                onChange={e => handleColorChange(ff.ref, e, i, 0, selectedDatasets[0])}
                presetColors={reportColors}
              />
            </div>
          ) : (
            selectedDatasets.map((ds, j) => (
              <div key={`${ds}-${ff.ref}`} className={styles.colorContainer}>
                <label htmlFor={`${ds}-${ff.ref}`}>
                  {ff.name} {'-'} {getDatasetName(ds)}
                </label>
                <ColorPicker
                  id={`${ds}-${ff.ref}-custom-color`}
                  color={customColors[i]?.[j]}
                  onChange={e => handleColorChange(ff.ref, e, i, j, ds)}
                  presetColors={reportColors}
                />
              </div>
            ))
          );
        })}
      </div>
    </>
  ) : null;
};
